// A small fishing hut on a jetty over the water: a stone footing carrying a
// plank deck, a timber hut on the land corner, a drying net on a frame, and a
// couple of landed fish on the deck. Ported 1:1 from age-of-mcp's fishing_hut.ts:
// a local deck() generator plus the shared foundationSlab/scaffold helpers.
import * as THREE from "three";
import { defineModel, benchWork, foundationSlab, scaffold, sway, EFFECT_LOOP, type Part } from "../../lib";

const DECK = "#9a7448";
const PILING = "#5e4528";
const HUT = "#7a5230";
const ROOF = "#4f3320";
const NET = "#caa86a";
const FISH = "#8aa6b0";

function deck(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(1.84, 0.12, 1.84), color: DECK, position: [0, 0.36, 0] },
  ];
  for (const [x, z] of [[-0.72, -0.72], [0.72, -0.72], [-0.72, 0.72], [0.72, 0.72]]) {
    parts.push({
      geometry: new THREE.CylinderGeometry(0.09, 0.09, 0.36, 5),
      color: PILING, position: [x!, 0.18, z!],
    });
  }
  return parts;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.84)];
  if (s === 1) return [foundationSlab(1.84), ...scaffold(1.6, 0.6)];
  if (s === 2) return [foundationSlab(1.84), ...deck(), ...scaffold(1.6, 0.85)];

  const top = 0.36 + 0.06;
  return [
    foundationSlab(1.84),
    ...deck(),
    { geometry: new THREE.BoxGeometry(0.7, 0.56, 0.7), color: HUT, position: [-0.5, top + 0.28, -0.5] },
    { geometry: new THREE.BoxGeometry(0.86, 0.12, 0.86), color: ROOF, position: [-0.5, top + 0.6, -0.5] },
    // The drying net, named so the "active" animation can sway it in the breeze.
    { geometry: new THREE.BoxGeometry(0.55, 0.5, 0.02), color: NET, position: [0.55, top + 0.3, 0.1], rotation: [0, 0.4, 0], name: "net" },
    { geometry: new THREE.BoxGeometry(0.16, 0.06, 0.06), color: FISH, position: [0.3, top + 0.02, 0.55], rotation: [0, 0.5, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.06, 0.06), color: FISH, position: [0.08, top + 0.02, 0.7], rotation: [0, -0.3, 0] },
  ];
}

export const fishingHut = defineModel({
  id: "fishing-hut",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Fishing hut",
  tags: ["building","fishing","hut","low-poly","age-of-mcp","classic","water","animated"],
  summary: "Low-poly fishing hut.",
  description: "A timber fishing hut on a jetty over the water, catching fish.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      // The drying net swings gently from the top rail of its frame.
      id: "active",
      label: "Nets swaying",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [sway("net", [0.55, 0.42 + 0.55, 0.1], { amount: 0.07 })],
    },
  ],
  stations: [
    { id: "deck", position: [0.45, 0.42, 0.78], facing: 0, description: "Where a fisher works the deck." },
  ],
  interactions: [
    { id: "fish", label: "Working the deck", station: "deck", variants: ["complete"], ...benchWork() },
  ],
});
